package fr.frux.feature.auth

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.logging.Logger
import fr.frux.shared.domain.repository.VerifyOutcome
import fr.frux.shared.domain.session.SessionState
import fr.frux.shared.domain.session.SessionStore
import fr.frux.shared.identity.IdentityPolicy
import fr.frux.shared.identity.IdentityProblem
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * ViewModel for the passwordless authentication screen (ADR 0012).
 *
 * Machine à trois étapes, portée par [AuthState.step] : [AuthStep.EnterEmail] → demande d'un code
 * → [AuthStep.EnterCode] → vérification → soit la session s'ouvre, soit (adresse sans compte)
 * [AuthStep.EnterUsername], qui re-soumet le **même** code accompagné d'un pseudo. L'état vit ici
 * et non dans un `remember` d'écran : Nav3 recompose l'entrée à chaque navigation (cf. #48 et
 * `FruxEntryProvider`), seul le ViewModel survit.
 */
class AuthViewModel(
    private val sessionStore: SessionStore,
) : ViewModel() {
    // Lu une fois à la construction, pas observé : c'est le constat qui a amené l'utilisateur ici,
    // pas un état à suivre. L'écran est atteint parce que la session vient d'être terminée (#48).
    private val _state =
        MutableStateFlow(
            AuthState(sessionExpired = sessionStore.state.value is SessionState.Expired),
        )
    val state: StateFlow<AuthState> = _state.asStateFlow()

    private var cooldownJob: Job? = null

    companion object {
        private const val TAG = "AuthViewModel"
    }

    fun onEmailChange(email: String) {
        _state.update { it.copy(email = email, error = null, sessionExpired = false) }
    }

    fun onCodeChange(code: String) {
        _state.update { it.copy(code = code, error = null, sessionExpired = false) }
    }

    fun onUsernameChange(username: String) {
        _state.update { it.copy(username = username, error = null, sessionExpired = false) }
    }

    /** Revenir à la saisie de l'email — abandonne le code en cours (#25, ADR 0012). */
    fun backToEmail() {
        cooldownJob?.cancel()
        _state.update {
            it.copy(
                step = AuthStep.EnterEmail,
                code = "",
                username = "",
                error = null,
                codeResent = false,
                resendCooldownSeconds = 0,
            )
        }
    }

    /**
     * Demande l'envoi d'un code à l'email saisi. Utilisé à la fois pour la première demande
     * (étape email) et pour « renvoyer un code » (étape code) : le serveur ne distingue pas les
     * deux, un nouvel envoi invalide simplement le précédent.
     */
    fun requestCode() {
        viewModelScope.launch {
            val currentState = _state.value
            val problem = IdentityPolicy.validateEmail(currentState.email)
            if (problem != null) {
                _state.update { it.copy(error = AuthError.Identity(problem)) }
                return@launch
            }

            Logger.d(TAG, "Requesting code: ${currentState.email}")
            _state.update { it.copy(isLoading = true, error = null) }

            // Depuis l'étape code, le même appel est un renvoi (#261) : c'est ce qui déclenche la
            // confirmation à l'écran, absente de la première demande où le changement d'étape suffit.
            val isResend = currentState.step == AuthStep.EnterCode

            sessionStore.requestCode(currentState.email).fold(
                onSuccess = { devCode ->
                    Logger.i(TAG, "✅ Code requested")
                    // devCode : écho du serveur en mode dev uniquement (null en production).
                    // Le préremplir épargne l'aller-retour vers les logs à chaque connexion de
                    // dev/test. Sans écho, on repart d'un champ vide : un nouvel envoi invalide
                    // le code précédent, un reliquat saisi n'a plus de valeur.
                    _state.update {
                        it.copy(
                            isLoading = false,
                            step = AuthStep.EnterCode,
                            code = devCode ?: "",
                            sessionExpired = false,
                            codeResent = isResend,
                        )
                    }
                    startResendCooldown()
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Code request failed", error)
                    _state.update { it.copy(isLoading = false, error = error.toAuthError()) }
                },
            )
        }
    }

    /**
     * Rend lisible l'anti-rafale du serveur (#261, #274) : pendant [IdentityPolicy.OTP_RESEND_COOLDOWN_SECONDS],
     * un renvoi serait accepté en apparence mais silencieusement ignoré (aucun nouvel email).
     * Désactiver le bouton avec un décompte évite de confirmer un envoi qui n'a pas eu lieu.
     * Démarre aussi à la première demande : la fenêtre du serveur court dès cet envoi-là.
     */
    private fun startResendCooldown() {
        cooldownJob?.cancel()
        cooldownJob =
            viewModelScope.launch {
                var remaining = IdentityPolicy.OTP_RESEND_COOLDOWN_SECONDS
                _state.update { it.copy(resendCooldownSeconds = remaining) }
                while (remaining > 0) {
                    delay(1_000)
                    remaining--
                    _state.update { it.copy(resendCooldownSeconds = remaining) }
                }
            }
    }

    /**
     * Vérifie le code saisi. Sur [AuthStep.EnterUsername], le pseudo est validé localement puis
     * envoyé avec le même code — ce n'est pas une nouvelle vérification, cf. ADR 0012.
     */
    fun verifyCode(onSuccess: () -> Unit) {
        viewModelScope.launch {
            val currentState = _state.value
            val validationError = currentState.validate()
            if (validationError != null) {
                _state.update { it.copy(error = validationError) }
                return@launch
            }

            Logger.d(TAG, "Verifying code for: ${currentState.email}")
            _state.update { it.copy(isLoading = true, error = null) }

            val username =
                if (currentState.step == AuthStep.EnterUsername) {
                    IdentityPolicy.normalizeUsername(currentState.username)
                } else {
                    null
                }

            sessionStore.verifyCode(currentState.email, currentState.code, username).fold(
                onSuccess = { outcome ->
                    when (outcome) {
                        is VerifyOutcome.Success -> {
                            Logger.i(TAG, "✅ Code verified: ${outcome.user.username}")
                            _state.update { it.copy(isLoading = false, sessionExpired = false) }
                            onSuccess()
                        }

                        VerifyOutcome.NeedsUsername -> {
                            _state.update {
                                it.copy(isLoading = false, step = AuthStep.EnterUsername, error = null)
                            }
                        }

                        VerifyOutcome.InvalidCode -> {
                            _state.update {
                                it.copy(isLoading = false, error = AuthError.CodeInvalidOrExpired)
                            }
                        }

                        VerifyOutcome.UsernameTaken -> {
                            _state.update {
                                it.copy(isLoading = false, error = AuthError.UsernameTaken)
                            }
                        }

                        VerifyOutcome.UsernameInvalid -> {
                            _state.update {
                                it.copy(
                                    isLoading = false,
                                    error = AuthError.Identity(IdentityProblem.USERNAME_INVALID),
                                )
                            }
                        }
                    }
                },
                onFailure = { error ->
                    Logger.e(TAG, "❌ Code verification failed", error)
                    _state.update { it.copy(isLoading = false, error = error.toAuthError()) }
                },
            )
        }
    }

    /**
     * Contrôles locaux avant appel réseau (#25) : email toujours revalidé (l'utilisateur a pu
     * revenir en arrière), code de la bonne longueur, pseudo requis seulement à sa propre étape.
     * `IdentityPolicy` vit dans `backend-shared`, compilé par les deux builds : ce formulaire ne
     * peut donc pas dériver de ce que l'API acceptera, elle ne fait que refléter la même règle.
     */
    private fun AuthState.validate(): AuthError? {
        val emailProblem = IdentityPolicy.validateEmail(email)
        val codeError = validateCode(code)
        val usernameProblem =
            if (step == AuthStep.EnterUsername) IdentityPolicy.validateUsername(username) else null
        return when {
            emailProblem != null -> AuthError.Identity(emailProblem)
            codeError != null -> codeError
            usernameProblem != null -> AuthError.Identity(usernameProblem)
            else -> null
        }
    }

    private fun validateCode(code: String): AuthError? =
        when {
            code.isEmpty() -> {
                AuthError.CodeRequired
            }

            code.length != IdentityPolicy.OTP_CODE_LENGTH || !code.all(Char::isDigit) -> {
                AuthError.CodeInvalidFormat
            }

            else -> {
                null
            }
        }
}

/** Étape courante du parcours passwordless (ADR 0012). */
enum class AuthStep { EnterEmail, EnterCode, EnterUsername }

/**
 * State for the passwordless Authentication screen.
 */
data class AuthState(
    val step: AuthStep = AuthStep.EnterEmail,
    val email: String = "",
    val code: String = "",
    val username: String = "",
    val isLoading: Boolean = false,
    val error: AuthError? = null,
    /**
     * La session précédente a été terminée par le serveur (401 non récupérable). Constat
     * informatif, pas une erreur de formulaire : ce n'est pas la faute de l'utilisateur (#48).
     */
    val sessionExpired: Boolean = false,
    /** Un nouveau code vient d'être renvoyé depuis l'étape code (#261), à confirmer à l'écran. */
    val codeResent: Boolean = false,
    /** Secondes restantes avant que le serveur accepte un nouvel envoi de code (#261, #274). */
    val resendCooldownSeconds: Int = 0,
)
